import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStripe } from "@stripe/react-stripe-js";

const API = process.env.REACT_APP_API_URL || "http://localhost:5001";

export default function PaymentReturn() {
  const [searchParams] = useSearchParams();
  const stripe = useStripe();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const paymentIntent = searchParams.get("payment_intent");
    const clientSecret = searchParams.get("payment_intent_client_secret");

    if (!paymentIntent || !clientSecret) {
      setStatus("error");
      setErrorMsg("Missing payment information.");
      return;
    }

    if (!stripe) return;

    const confirmAndFetchOrder = async () => {
      try {
        // 1. Retrieve PaymentIntent status
        const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);

        if (paymentIntent.status !== "succeeded") {
          setStatus("failed");
          return;
        }

        setStatus("success");

        // 2. Fetch the real order ID from your backend
        const token = localStorage.getItem("sf_token");
        if (!token) {
          setErrorMsg("You are not logged in. Please log in to view your order.");
          // Still consider payment successful, just can't fetch order.
          // Maybe redirect to login or orders page after a delay.
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        const res = await fetch(`${API}/api/orders/by-payment-intent/${paymentIntent.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          const data = await res.json();
          localStorage.removeItem("sf_pending_order");
          setTimeout(() => navigate(`/order-confirmation/${data.orderId}`), 3000);
        } else {
          // No order yet — this payment method redirected the customer away
          // (e.g. 3D Secure) before Checkout.jsx could create the order.
          // Fall back to creating it now from the saved cart data.
          const pending = localStorage.getItem("sf_pending_order");
          if (pending) {
            try {
              const createRes = await fetch(`${API}/api/orders`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  ...JSON.parse(pending),
                  stripe_payment_id: paymentIntent.id,
                }),
              });
              const createData = await createRes.json();
              if (createRes.ok) {
                localStorage.removeItem("sf_pending_order");
                await fetch(`${API}/api/cart/clear`, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${token}` },
                });
                setTimeout(() => navigate(`/order-confirmation/${createData.id}`), 3000);
                return;
              }
            } catch (e) {
              console.error("Fallback order creation failed:", e);
            }
          }
          console.warn("Order not found for payment intent:", paymentIntent.id);
          setErrorMsg("We're still processing your order. You will be redirected to your orders shortly.");
          setTimeout(() => navigate("/orders"), 5000);
        }
      } catch (err) {
        console.error("Error in PaymentReturn:", err);
        setStatus("error");
        setErrorMsg("An unexpected error occurred. Please contact support.");
      }
    };

    confirmAndFetchOrder();
  }, [stripe, searchParams, navigate]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-lg font-semibold">Processing your payment...</p>
          <p className="text-sm text-gray-500">Please wait while we confirm your order.</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-4xl mb-4">😕</div>
          <p className="text-lg font-semibold text-red-600">Something went wrong</p>
          <p className="text-sm text-gray-600">{errorMsg || "Please contact support."}</p>
          <button
            onClick={() => navigate("/")}
            className="mt-4 px-6 py-2 bg-stone-900 text-white rounded-xl hover:bg-stone-700 transition"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🎉</div>
          <p className="text-2xl font-bold text-green-600">Payment successful!</p>
          <p className="text-sm text-gray-600 mt-2">
            {errorMsg || "Your order is being processed. You will be redirected shortly."}
          </p>
          <div className="mt-6 flex justify-center gap-4">
            <button
              onClick={() => navigate("/orders")}
              className="px-6 py-2 bg-stone-900 text-white rounded-xl hover:bg-stone-700 transition"
            >
              View My Orders
            </button>
            <button
              onClick={() => navigate("/shop")}
              className="px-6 py-2 border border-stone-300 rounded-xl hover:bg-stone-50 transition"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  // failed
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center max-w-md">
        <div className="text-4xl mb-4">❌</div>
        <p className="text-lg font-semibold text-red-600">Payment failed</p>
        <p className="text-sm text-gray-600">Please try again or use a different payment method.</p>
        <button
          onClick={() => navigate("/checkout")}
          className="mt-4 px-6 py-2 bg-stone-900 text-white rounded-xl hover:bg-stone-700 transition"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}